/**
 * The Team card on the home (staff chat MVP, SR-14; Samer: "on the dashboard for practice and practitioners"), ported
 * from Antonin's catalog source (Signal DS `catalog/src/components/TeamChat/TeamChat.tsx`, `TeamCard`): under Needs you,
 * the home's card shell (HomeCard) titled "Team"; "N unread" (the same total as the dock's bar) and New message on the
 * right; five conversations, unread first; All messages unfolds every conversation with a search, in place (Show fewer
 * folds it). A row opens the dock on that conversation; New message opens the dock on New message.
 *
 * Renders nothing with the `?x=staff-chat` switch off.
 */
import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { HomeCard } from '@/dashboard/ready-list/home-card';
import { isStaffChatOn } from '@/staff-chat/lib/staff-chat-switch';
import { useDockStore } from '@/staff-chat/lib/dock-store';
import { useHubStatusStore } from '@/staff-chat/lib/hub-status-store';
import { threadTitle, unreadFirst } from '@/staff-chat/lib/chat-format';
import { useStaffChatIdentity, useStaffChatUnread, useStaffThreads } from '@/staff-chat/services/use-staff-chat';
import { NO_ACCESS_TITLE, staffChatErrorKind, UNAVAILABLE_TITLE } from '@/staff-chat/lib/staff-chat-errors';
import { TeamRow } from './team-row';
import { Reconnecting, SkeletonLines } from './team-chat-parts';

export const HOME_THREADS = 5;

export function TeamHomeCard() {
  if (!isStaffChatOn()) return null;
  return <TeamCard />;
}

function TeamCard() {
  const { me } = useStaffChatIdentity();
  const threads = useStaffThreads();
  const { data: unread = 0 } = useStaffChatUnread();
  const openDock = useDockStore((s) => s.openDock);
  const startNewMessage = useDockStore((s) => s.startNewMessage);
  const reconnecting = useHubStatusStore((s) => s.status === 'reconnecting');
  const [all, setAll] = useState(false);
  const [q, setQ] = useState('');

  const conversations = useMemo(() => threads.data?.items ?? [], [threads.data]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const found = t ? conversations.filter((c) => threadTitle(c, me).toLowerCase().includes(t)) : conversations;
    return unreadFirst(found).slice(0, all ? undefined : HOME_THREADS);
  }, [conversations, q, all, me]);

  const right = (
    <>
      {unread > 0 ? (
        <span data-team-unread className="home-pill">
          {unread} unread
        </span>
      ) : null}
      <Button type="button" variant="tertiary" size="sm" icon={<Plus aria-hidden />} onClick={() => startNewMessage()}>
        New message
      </Button>
    </>
  );

  const kind = threads.isError && !threads.data ? staffChatErrorKind(threads.error) : null;
  let body;
  if (threads.isPending)
    body = (
      <div className="team-chat-pad">
        <SkeletonLines label="Loading conversations" />
      </div>
    );
  else if (kind === 'no-access' || kind === 'unavailable')
    body = <p className="team-chat-note team-chat-pad">{kind === 'no-access' ? NO_ACCESS_TITLE : UNAVAILABLE_TITLE}</p>;
  else if (threads.isError && !threads.data)
    body = (
      <p className="team-chat-note team-chat-pad">
        Couldn&apos;t load messages.{' '}
        <button type="button" className="team-chat-link" onClick={() => void threads.refetch()}>
          Retry
        </button>
      </p>
    );
  else if (conversations.length === 0) body = <p className="team-chat-note team-chat-pad">No messages yet. Write to a colleague with New message.</p>;
  else
    body = (
      <>
        {reconnecting ? <Reconnecting /> : null}
        {all ? (
          <div className="team-chat-search">
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" aria-label="Search conversations" />
          </div>
        ) : null}
        <div className="team-chat-rows" data-all={all || undefined} role="list" aria-label="Latest conversations">
          {rows.length ? (
            rows.map((t) => (
              <div role="listitem" key={t.id}>
                <TeamRow thread={t} me={me} onClick={() => openDock(t.id)} />
              </div>
            ))
          ) : (
            <p className="team-chat-note team-chat-pad">No one matches that name.</p>
          )}
        </div>
        {conversations.length > HOME_THREADS ? (
          <div className="team-chat-more">
            <button
              type="button"
              className="team-chat-link"
              onClick={() => {
                setAll(!all);
                setQ('');
              }}
            >
              {all ? 'Show fewer' : 'All messages'}
            </button>
          </div>
        ) : null}
      </>
    );

  return (
    <HomeCard title="Team" data-testid="team-home-card" right={right} flush>
      {body}
    </HomeCard>
  );
}
